import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    applyNodeChanges,
    MarkerType,
    type Connection,
    type Edge,
    type OnNodeDrag,
    type OnNodesChange,
} from '@xyflow/react';
import {
    type ProcessExecuteIntervalType,
    type ProcessIntervalAnchor,
    type ProcessTemplateTaskType,
} from '@econtractor/types';
import { formatProcessInterval } from '../helpers/formatInterval';
import {
    PROCESS_DIAGRAM_TARGET_HANDLE,
    parseSourceHandleAnswerKey,
    sourceHandleIdFromAnswerKey,
} from '../helpers/handles';
import { classifyProcessDiagram } from '../helpers/diagramProgress';
import { layoutProcessDiagramTasks } from '../helpers/layoutTasks';
import {
    addTaskNextTask,
    getProcessNextTasks,
    getProcessPosition,
    isSameProcessTaskInstance,
    parseProcessTaskInstanceKey,
    processTaskInstanceKey,
    removeTaskNextTask,
    setTaskNextTaskInterval,
    setTaskPosition,
} from '../helpers/nextTask';
import { wouldCreateProcessCycle } from '../helpers/willCreateCycle';
import type { ProcessDiagramEdgeType } from '../ProcessDiagramEdge';
import type { ProcessDiagramNodeType } from '../ProcessDiagramNode';

export type ProcessDiagramTaskType = ProcessTemplateTaskType & {
    instanceStatus?: number | null;
    instanceAnswerKey?: number | null;
    instancePending?: boolean;
};

const ACTIVE_EDGE_MARKER = {
    type: MarkerType.ArrowClosed,
    color: 'var(--bs-primary)',
    width: 14,
    height: 14,
    markerUnits: 'userSpaceOnUse',
};

type UseProcessDiagramParams = {
    tasks: ProcessDiagramTaskType[];
    readOnly?: boolean;
    onChange: (tasks: ProcessDiagramTaskType[]) => void;
    onIntervalClick: (sourceTemplateId: number, sourceDtId: number, templateId: number, dtId: number, answerKey: number | null) => void;
};

export const useProcessDiagram = ({ tasks, readOnly = false, onChange, onIntervalClick }: UseProcessDiagramParams) => {
    const { t } = useTranslation(['Tasks']);
    const onIntervalClickRef = useRef(onIntervalClick);
    onIntervalClickRef.current = onIntervalClick;
    const draggingRef = useRef(false);

    const taskNameByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const task of tasks) map.set(processTaskInstanceKey(task.templateId, task.dtId), task.taskName);
        return map;
    }, [tasks]);

    const layout = useMemo(() => layoutProcessDiagramTasks(tasks), [tasks]);
    const progress = useMemo(() => classifyProcessDiagram(tasks), [tasks]);

    const nodesFromTasks = useMemo<ProcessDiagramNodeType[]>(
        () =>
            tasks.map((task) => {
                const outgoing = getProcessNextTasks(task.processData).map((link) => ({
                    templateId: link.templateId,
                    dtId: link.dtId,
                    name: taskNameByKey.get(processTaskInstanceKey(link.templateId, link.dtId)) || task.taskName,
                    answerKey: link.answerKey,
                    interval: link.interval,
                    intervalAnchor: link.intervalAnchor,
                }));
                const instanceKey = processTaskInstanceKey(task.templateId, task.dtId);
                return {
                    id: instanceKey,
                    type: 'processTask' as const,
                    position: getProcessPosition(task.processData) ?? layout.get(instanceKey) ?? { x: 0, y: 0 },
                    dragHandle: '.process-diagram-node-drag',
                    data: {
                        task,
                        readOnly,
                        pruned: progress.prunedKeys.has(instanceKey),
                        current: progress.currentKeys.has(instanceKey),
                        outgoing,
                        onIntervalClick: (templateId: number, dtId: number, answerKey: number | null) =>
                            onIntervalClickRef.current(task.templateId, task.dtId, templateId, dtId, answerKey),
                    },
                };
            }),
        [layout, progress, readOnly, taskNameByKey, tasks],
    );

    const [nodes, setNodes] = useState(nodesFromTasks);

    useEffect(() => {
        if (draggingRef.current) return;
        setNodes(nodesFromTasks);
    }, [nodesFromTasks]);

    const edges = useMemo<ProcessDiagramEdgeType[]>(() => {
        const result: ProcessDiagramEdgeType[] = [];
        for (const task of tasks) {
            const sourceKey = processTaskInstanceKey(task.templateId, task.dtId);
            for (const link of getProcessNextTasks(task.processData)) {
                const targetKey = processTaskInstanceKey(link.templateId, link.dtId);
                if (!taskNameByKey.has(targetKey)) continue;
                const role = progress.edgeRole(sourceKey, targetKey, link.answerKey);
                const inactive = role === 'inactive';
                const active = role === 'active';
                result.push({
                    id: `next-${sourceKey}-${targetKey}-${link.answerKey ?? 'none'}`,
                    source: sourceKey,
                    target: targetKey,
                    sourceHandle: sourceHandleIdFromAnswerKey(link.answerKey),
                    targetHandle: PROCESS_DIAGRAM_TARGET_HANDLE,
                    className: active
                        ? 'process-diagram-edge--active'
                        : inactive
                          ? 'process-diagram-edge--inactive'
                          : undefined,
                    zIndex: active ? 2 : 0,
                    markerEnd: active ? ACTIVE_EDGE_MARKER : undefined,
                    data: {
                        label: formatProcessInterval(link.interval, t, link.intervalAnchor),
                        interval: link.interval,
                        intervalAnchor: link.intervalAnchor ?? null,
                        inactive,
                    },
                    style: active
                        ? { stroke: 'var(--bs-primary)', strokeWidth: 2.5 }
                        : inactive
                          ? { opacity: 0.45 }
                          : undefined,
                });
            }
        }
        return result;
    }, [progress, t, taskNameByKey, tasks]);

    const handleConnect = useCallback(
        ({ source, target, sourceHandle }: Connection) => {
            if (readOnly) return;
            const sourceInst = parseProcessTaskInstanceKey(source);
            const targetInst = parseProcessTaskInstanceKey(target);
            if (!sourceInst || !targetInst) return;
            if (
                wouldCreateProcessCycle(
                    tasks,
                    sourceInst.templateId,
                    sourceInst.dtId,
                    targetInst.templateId,
                    targetInst.dtId,
                )
            ) {
                return;
            }
            const answerKey = parseSourceHandleAnswerKey(sourceHandle);
            onChange(
                tasks.map((task) =>
                    isSameProcessTaskInstance(task, sourceInst.templateId, sourceInst.dtId)
                        ? addTaskNextTask(task, {
                              templateId: targetInst.templateId,
                              dtId: targetInst.dtId,
                              answerKey,
                              interval: null,
                          })
                        : task,
                ),
            );
        },
        [onChange, readOnly, tasks],
    );

    const isValidConnection = useCallback(
        (connection: Edge | Connection): boolean => {
            if (readOnly) return false;
            const sourceInst = parseProcessTaskInstanceKey(connection.source);
            const targetInst = parseProcessTaskInstanceKey(connection.target);
            if (!sourceInst || !targetInst) return false;
            return !wouldCreateProcessCycle(
                tasks,
                sourceInst.templateId,
                sourceInst.dtId,
                targetInst.templateId,
                targetInst.dtId,
            );
        },
        [readOnly, tasks],
    );

    const handleEdgeClick = useCallback(
        (_event: MouseEvent, edge: ProcessDiagramEdgeType) => {
            if (readOnly) return;
            const sourceInst = parseProcessTaskInstanceKey(edge.source);
            const targetInst = parseProcessTaskInstanceKey(edge.target);
            if (!sourceInst || !targetInst) return;
            const answerKey = parseSourceHandleAnswerKey(edge.sourceHandle);
            onChange(
                tasks.map((task) =>
                    isSameProcessTaskInstance(task, sourceInst.templateId, sourceInst.dtId)
                        ? removeTaskNextTask(task, {
                              templateId: targetInst.templateId,
                              dtId: targetInst.dtId,
                              answerKey,
                          })
                        : task,
                ),
            );
        },
        [onChange, readOnly, tasks],
    );

    const handleNodesChange: OnNodesChange<ProcessDiagramNodeType> = useCallback((changes) => {
        const kept = changes.filter((change) => change.type !== 'remove');
        setNodes((current) => applyNodeChanges(kept, current));
    }, []);

    const handleNodeDragStart: OnNodeDrag<ProcessDiagramNodeType> = useCallback(() => {
        draggingRef.current = true;
    }, []);

    const handleNodeDragStop: OnNodeDrag<ProcessDiagramNodeType> = useCallback(
        (_event, node) => {
            draggingRef.current = false;
            if (readOnly) return;
            const inst = parseProcessTaskInstanceKey(node.id);
            if (!inst) return;
            onChange(
                tasks.map((task) =>
                    isSameProcessTaskInstance(task, inst.templateId, inst.dtId)
                        ? setTaskPosition(task, node.position)
                        : task,
                ),
            );
        },
        [onChange, readOnly, tasks],
    );

    const handleAutoArrange = useCallback(() => {
        if (readOnly) return;
        const arranged = layoutProcessDiagramTasks(tasks);
        onChange(
            tasks.map((task) => {
                const position = arranged.get(processTaskInstanceKey(task.templateId, task.dtId));
                return position ? setTaskPosition(task, position) : task;
            }),
        );
    }, [onChange, readOnly, tasks]);

    const updateLinkInterval = useCallback(
        (
            sourceTemplateId: number,
            sourceDtId: number,
            templateId: number,
            dtId: number,
            answerKey: number | null,
            interval: ProcessExecuteIntervalType | null,
            intervalAnchor: ProcessIntervalAnchor | null,
        ) => {
            onChange(
                tasks.map((task) =>
                    isSameProcessTaskInstance(task, sourceTemplateId, sourceDtId)
                        ? setTaskNextTaskInterval(task, { templateId, dtId, answerKey }, interval, intervalAnchor)
                        : task,
                ),
            );
        },
        [onChange, tasks],
    );

    return {
        nodes,
        edges,
        handleConnect,
        isValidConnection,
        handleEdgeClick,
        handleNodesChange,
        handleNodeDragStart,
        handleNodeDragStop,
        handleAutoArrange,
        updateLinkInterval,
    };
};
